import {
  type PreparedShape,
  type Rng,
  type ShapeDefinition,
  type ShapeSink,
  type Vec3,
} from '../shape.js';
import { alongSegment, hash01, inBox, onDisc, revolve, spinZ, TAU } from './helpers.js';

/* ── Library ───────────────────────────────────────────────────────────── */

interface Book {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly width: number;
  readonly height: number;
  readonly lean: number;
  readonly tint: number;
}

const SHELF_ROWS = 6;
const SHELF_RISE = 1.5;
const WALL_HALF = 9;

function buildBooks(random: Rng, variant: number): Book[] {
  const books: Book[] = [];
  const gapChance = 0.06 + variant * 0.1;
  for (let row = 0; row < SHELF_ROWS; row += 1) {
    let x = -WALL_HALF;
    while (x < WALL_HALF) {
      const width = 0.11 + random() * 0.24;
      if (random() > gapChance) {
        books.push({
          x: x + width / 2,
          y: row * SHELF_RISE + 0.06,
          z: (random() - 0.5) * 0.12,
          width,
          height: 0.85 + random() * 0.42,
          lean: random() < 0.08 ? (random() - 0.5) * 0.5 : 0,
          // Spread spine colours across the middle of the palette so no two
          // neighbours match — a shelf of identical books reads as a wall.
          tint: 0.2 + hash01(books.length * 7.13) * 0.55,
        });
      }
      x += width + 0.015;
    }
  }
  return books;
}

/** One book: a leaning slab with a cloth spine and gilded page edges. */
function bookPoint(sink: ShapeSink, random: Rng, book: Book): void {
  inBox(sink, random, { x: book.width / 2, y: book.height / 2, z: 0.16 });
  spinZ(sink, book.lean);
  sink.x += book.x;
  sink.y += book.y + book.height / 2;
  sink.z += book.z;
  sink.tint = sink.z > 0.1 ? 0.9 + random() * 0.1 : book.tint;
}

/** Shelf boards and the uprights they sit in. */
function shelfPoint(sink: ShapeSink, random: Rng): void {
  const isUpright = random() < 0.22;
  if (isUpright) {
    sink.x = (random() < 0.5 ? -1 : 1) * WALL_HALF;
    sink.y = random() * SHELF_ROWS * SHELF_RISE;
  } else {
    sink.x = (random() - 0.5) * WALL_HALF * 2;
    sink.y = Math.floor(random() * (SHELF_ROWS + 1)) * SHELF_RISE + (random() - 0.5) * 0.08;
  }
  sink.z = (random() - 0.5) * 0.5;
  sink.tint = 0.06 + random() * 0.1;
}

function placeLibrary(sink: ShapeSink, random: Rng, books: readonly Book[], p: number): void {
  if (p < 0.68) {
    const book = books[Math.floor(random() * books.length)];
    if (book !== undefined) {
      bookPoint(sink, random, book);
    }
  } else if (p < 0.84) {
    shelfPoint(sink, random);
  } else if (p < 0.92) {
    // Floorboards in front of the shelves.
    sink.x = (random() - 0.5) * WALL_HALF * 2.2;
    sink.y = -0.05;
    sink.z = 0.4 + random() * 7;
    sink.tint = 0.1 + (Math.abs(Math.sin(sink.x * 6)) > 0.95 ? 0.2 : 0);
  } else {
    // Pages breaking loose and drifting into the room.
    const t = random();
    sink.x = (random() - 0.5) * 12;
    sink.y = 1 + t * 7;
    sink.z = 1 + t * 6 + random();
    sink.tint = 0.94 + random() * 0.06;
    sink.driftX = 0.5;
    sink.driftY = 0.35;
    sink.driftZ = 0.3;
    sink.phase = t * TAU;
  }
}

export const library: ShapeDefinition = {
  name: 'library',
  palette: { base: 0x6a_30_20, shade: 0xd2_70_4a, accent: 0xff_f0_cf },
  driftRate: 0.5,
  framing: 1.76,
  prepare: ({ random, total, variant }): PreparedShape => {
    const books = buildBooks(random, variant);
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeLibrary(sink, random, books, index / total);
      },
    };
  },
};

/* ── Room: table, chairs, lamp, rug ────────────────────────────────────── */

const TABLE_TOP = 2.6;
const TABLE_HALF = 3.4;
const LAMP_BASE: Vec3 = { x: -5.4, y: 0, z: -1.2 };
const LAMP_TOP: Vec3 = { x: -5.4, y: 5.2, z: -1.2 };

function tableLeg(sink: ShapeSink, random: Rng, sx: number, sz: number): void {
  const from: Vec3 = { x: sx * (TABLE_HALF - 0.4), y: 0, z: sz * (TABLE_HALF * 0.6 - 0.4) };
  const to: Vec3 = { x: from.x, y: TABLE_TOP, z: from.z };
  alongSegment(sink, random, from, to, 0.16);
}

function chairAt(sink: ShapeSink, random: Rng, offsetX: number, facing: number): void {
  const seat = 1.5;
  const part = random();
  if (part < 0.34) {
    inBox(sink, random, { x: 0.7, y: 0.07, z: 0.7 });
    sink.y += seat;
  } else if (part < 0.72) {
    // Back with vertical slats.
    const slat = Math.round((random() * 2 - 1) * 3) / 3;
    sink.x = slat * 0.66;
    sink.y = seat + random() * 1.5;
    sink.z = -0.66;
  } else {
    const sx = random() < 0.5 ? -1 : 1;
    const sz = random() < 0.5 ? -1 : 1;
    const from: Vec3 = { x: sx * 0.6, y: 0, z: sz * 0.6 };
    alongSegment(sink, random, from, { x: from.x, y: seat, z: from.z }, 0.09);
  }
  const c = Math.cos(facing);
  const s = Math.sin(facing);
  const x = sink.x;
  sink.x = x * c - sink.z * s + offsetX;
  sink.z = x * s + sink.z * c;
}

/** The standing lamp: a pole and a glowing conical shade. */
function lampPoint(sink: ShapeSink, random: Rng): void {
  if (random() < 0.45) {
    alongSegment(sink, random, LAMP_BASE, LAMP_TOP, 0.07);
    sink.tint = 0.3;
    return;
  }
  revolve(sink, random, (v) => 1.25 - v * 0.55, 1.3);
  sink.x += LAMP_TOP.x;
  sink.y += LAMP_TOP.y - 1.3;
  sink.z += LAMP_TOP.z;
  sink.tint = 0.9 + random() * 0.1;
}

function placeRoom(sink: ShapeSink, random: Rng, facing: number, p: number): void {
  if (p < 0.2) {
    inBox(sink, random, { x: TABLE_HALF, y: 0.09, z: TABLE_HALF * 0.6 });
    sink.y += TABLE_TOP;
    sink.tint = 0.4 + random() * 0.16;
  } else if (p < 0.32) {
    tableLeg(sink, random, random() < 0.5 ? -1 : 1, random() < 0.5 ? -1 : 1);
    sink.tint = 0.28 + random() * 0.1;
  } else if (p < 0.58) {
    chairAt(sink, random, random() < 0.5 ? -5.2 : 5.2, facing + (random() < 0.5 ? 0 : Math.PI));
    sink.tint = 0.34 + random() * 0.14;
  } else if (p < 0.7) {
    lampPoint(sink, random);
  } else if (p < 0.86) {
    // A patterned rug under the table.
    onDisc(sink, random, 8.5);
    sink.x *= 1.25;
    sink.y = 0.01;
    const rings = Math.abs(Math.sin(Math.hypot(sink.x * 0.8, sink.z) * 1.7));
    sink.tint = 0.12 + rings * 0.35;
  } else {
    // Warm light pooling on the floor and dust in the air.
    onDisc(sink, random, 11);
    sink.y = random() * 4.5;
    sink.tint = 0.86 + random() * 0.14;
    sink.driftY = 0.3;
    sink.driftX = 0.2;
    sink.phase = random() * TAU;
  }
}

export const room: ShapeDefinition = {
  name: 'room',
  palette: { base: 0x5a_34_2a, shade: 0xba_7a_4c, accent: 0xff_dd_9c },
  driftRate: 0.35,
  framing: 2.56,
  prepare: ({ random, total, variant }): PreparedShape => {
    const facing = variant * 0.6;
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeRoom(sink, random, facing, index / total);
      },
    };
  },
};

/* ── Clock ─────────────────────────────────────────────────────────────── */

const FACE_RADIUS = 5.4;

function handPoint(sink: ShapeSink, random: Rng, angle: number, length: number): void {
  const t = random();
  const spread = (random() - 0.5) * 0.16;
  sink.x = Math.sin(angle) * length * t + Math.cos(angle) * spread;
  sink.y = Math.cos(angle) * length * t - Math.sin(angle) * spread;
  sink.z = 0.35;
}

interface ClockHands {
  readonly hour: number;
  readonly minute: number;
}

/** Numerals and minute ticks around the dial. */
function clockTick(sink: ShapeSink, random: Rng): void {
  const tick = Math.floor(random() * 60);
  const isMajor = tick % 5 === 0;
  const angle = (tick / 60) * TAU;
  const r = FACE_RADIUS - (isMajor ? 0.7 : 0.35) * random() - 0.15;
  sink.x = Math.sin(angle) * r;
  sink.y = Math.cos(angle) * r;
  sink.z = 0.22;
  sink.tint = isMajor ? 0.92 : 0.6;
}

/** The pendulum: a rod that sweeps, and the bob on the end of it. */
function pendulumPoint(sink: ShapeSink, random: Rng): void {
  if (random() < 0.5) {
    sink.x = (random() - 0.5) * 0.14;
    sink.y = -FACE_RADIUS - random() * 4.5;
    sink.z = 0;
    sink.tint = 0.45;
    sink.driftX = ((-sink.y - FACE_RADIUS) / 4.5) * 1.6;
    return;
  }
  onDisc(sink, random, 0.85);
  sink.y = sink.z - FACE_RADIUS - 5;
  sink.z = 0;
  sink.tint = 0.8 + random() * 0.2;
  sink.driftX = 1.6;
}

function placeClock(sink: ShapeSink, random: Rng, hands: ClockHands, p: number): void {
  if (p < 0.3) {
    onDisc(sink, random, FACE_RADIUS);
    sink.y = sink.z;
    sink.z = (random() - 0.5) * 0.2;
    sink.tint = 0.1 + random() * 0.08;
  } else if (p < 0.5) {
    const angle = random() * TAU;
    const r = FACE_RADIUS + 0.1 + random() * 0.45;
    sink.x = Math.cos(angle) * r;
    sink.y = Math.sin(angle) * r;
    sink.z = (random() - 0.5) * 0.5;
    sink.tint = 0.5 + random() * 0.2;
  } else if (p < 0.68) {
    clockTick(sink, random);
  } else if (p < 0.86) {
    const isHour = random() < 0.5;
    handPoint(sink, random, isHour ? hands.hour : hands.minute, isHour ? 3 : 4.6);
    sink.tint = 0.88 + random() * 0.12;
  } else {
    pendulumPoint(sink, random);
  }
}

export const clock: ShapeDefinition = {
  name: 'clock',
  palette: { base: 0x3a_38_50, shade: 0xd4_c4_96, accent: 0xff_f6_dc },
  driftRate: 0.9,
  framing: 2.29,
  prepare: ({ random, total, variant }): PreparedShape => {
    const hands: ClockHands = { hour: variant * TAU, minute: ((variant * 12) % 1) * TAU };
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeClock(sink, random, hands, index / total);
      },
    };
  },
};
